// components/island/commands.ts
// Befehls-Registry für die ⌘K-Palette (Dynamic Island).

import {
  LayoutDashboard, Building2, UserCircle, Contact, Files, Gauge, Mail,
  TicketCheck, CalendarDays, FileText, Receipt, Users, ShieldCheck,
  UserPlus, PlusCircle, LogOut, type LucideIcon,
} from 'lucide-react';
import { fuzzyScore } from '../../lib/island/fuzzy';
import { supabase } from '../../lib/supabase/client';

export type CommandCtx = {
  navigate: (path: string) => void;
  onSearchNavigate?: (path: string, id: string, type: string) => void;
  collapse: () => void;
};

export type Command = {
  id: string;
  label: string;
  hint?: string;
  icon?: LucideIcon;
  keywords?: string[];
  run: (ctx: CommandCtx) => void;
};

// ---------------------------------------------------------------------------
// Navigations-Befehle (reale Admin-Routen, Labels = Sidebar-Wording)
// ---------------------------------------------------------------------------

export const COMMANDS: Command[] = [
  // --- Top-Level ---
  {
    id: 'nav-dashboard',
    label: 'Dashboard',
    hint: 'Seite',
    icon: LayoutDashboard,
    keywords: ['start', 'home', 'cockpit', 'übersicht'],
    run: ({ navigate, collapse }) => { navigate('/admin/dashboard'); collapse(); },
  },

  // --- Verwaltung ---
  {
    id: 'nav-objekte',
    label: 'Immobilien',
    hint: 'Seite',
    icon: Building2,
    keywords: ['objekte', 'häuser', 'liegenschaften', 'gebäude', 'wohnungen'],
    run: ({ navigate, collapse }) => { navigate('/admin/objekte'); collapse(); },
  },
  {
    id: 'nav-eigentuemer',
    label: 'Eigentümer',
    hint: 'Seite',
    icon: UserCircle,
    keywords: ['eigentümer', 'besitzer'],
    run: ({ navigate, collapse }) => { navigate('/admin/eigentuemer'); collapse(); },
  },
  {
    id: 'nav-kontakte',
    label: 'Kontakte',
    hint: 'Seite',
    icon: Contact,
    keywords: ['personen', 'adressen', 'ansprechpartner'],
    run: ({ navigate, collapse }) => { navigate('/admin/kontakte'); collapse(); },
  },
  {
    id: 'nav-dokumente',
    label: 'Dokumente',
    hint: 'Seite',
    icon: Files,
    keywords: ['dateien', 'pdfs', 'unterlagen'],
    run: ({ navigate, collapse }) => { navigate('/admin/dokumente'); collapse(); },
  },
  {
    id: 'nav-zaehler',
    label: 'Zähler',
    hint: 'Seite',
    icon: Gauge,
    keywords: ['strom', 'wasser', 'gas', 'nebenkosten', 'ablesungen'],
    run: ({ navigate, collapse }) => { navigate('/admin/zaehler'); collapse(); },
  },
  {
    id: 'nav-emails',
    label: 'E-Mails',
    hint: 'Seite',
    icon: Mail,
    keywords: ['posteingang', 'nachrichten', 'mails'],
    run: ({ navigate, collapse }) => { navigate('/admin/emails'); collapse(); },
  },

  // --- Vermietung ---
  {
    id: 'nav-mieter',
    label: 'Mieter',
    hint: 'Seite',
    icon: Users,
    keywords: ['mieter', 'bewohner', 'mietparteien'],
    run: ({ navigate, collapse }) => { navigate('/admin/mieter'); collapse(); },
  },
  {
    id: 'nav-vertraege',
    label: 'Mietverträge',
    hint: 'Seite',
    icon: FileText,
    keywords: ['verträge', 'mietvertrag', 'konditionen'],
    run: ({ navigate, collapse }) => { navigate('/admin/vertraege'); collapse(); },
  },
  {
    id: 'nav-tickets',
    label: 'Tickets',
    hint: 'Seite',
    icon: TicketCheck,
    keywords: ['aufgaben', 'anfragen', 'support', 'mängel'],
    run: ({ navigate, collapse }) => { navigate('/admin/tickets'); collapse(); },
  },
  {
    id: 'nav-kalender',
    label: 'Kalender',
    hint: 'Seite',
    icon: CalendarDays,
    keywords: ['termine', 'events', 'datum'],
    run: ({ navigate, collapse }) => { navigate('/admin/kalender'); collapse(); },
  },

  // --- Buchhaltung ---
  {
    id: 'nav-rechnungen',
    label: 'Rechnungen',
    hint: 'Seite',
    icon: Receipt,
    keywords: ['buchhaltung', 'kosten', 'zahlungen', 'belege'],
    run: ({ navigate, collapse }) => { navigate('/admin/rechnungen'); collapse(); },
  },

  // --- System ---
  {
    id: 'nav-team',
    label: 'Team',
    hint: 'Seite',
    icon: ShieldCheck,
    keywords: ['mitarbeiter', 'nutzer', 'berechtigungen'],
    run: ({ navigate, collapse }) => { navigate('/admin/team'); collapse(); },
  },

  // ---------------------------------------------------------------------------
  // Aktions-Befehle
  // ---------------------------------------------------------------------------
  {
    id: 'act-neuer-mieter',
    label: 'Neuer Mieter',
    hint: 'Aktion',
    icon: UserPlus,
    keywords: ['anlegen', 'erstellen', 'hinzufügen', 'mieter'],
    run: ({ navigate, collapse }) => { navigate('/admin/mieter'); collapse(); },
  },
  {
    id: 'act-neuer-eigentuemer',
    label: 'Neuer Eigentümer',
    hint: 'Aktion',
    icon: UserPlus,
    keywords: ['anlegen', 'erstellen', 'hinzufügen', 'eigentümer'],
    run: ({ navigate, collapse }) => { navigate('/admin/eigentuemer'); collapse(); },
  },
  {
    id: 'act-neuer-kontakt',
    label: 'Neuer Kontakt',
    hint: 'Aktion',
    icon: UserPlus,
    keywords: ['anlegen', 'erstellen', 'hinzufügen', 'kontakt'],
    run: ({ navigate, collapse }) => { navigate('/admin/kontakte'); collapse(); },
  },
  {
    id: 'act-neues-ticket',
    label: 'Neues Ticket',
    hint: 'Aktion',
    icon: PlusCircle,
    keywords: ['anlegen', 'erstellen', 'hinzufügen', 'aufgabe', 'anfrage'],
    run: ({ navigate, collapse }) => { navigate('/admin/tickets'); collapse(); },
  },
  {
    id: 'act-neue-rechnung',
    label: 'Neue Rechnung',
    hint: 'Aktion',
    icon: PlusCircle,
    keywords: ['anlegen', 'erstellen', 'hinzufügen', 'rechnung', 'buchhaltung'],
    run: ({ navigate, collapse }) => { navigate('/admin/rechnungen'); collapse(); },
  },

  // ---------------------------------------------------------------------------
  // Sitzung
  // ---------------------------------------------------------------------------
  {
    id: 'act-abmelden',
    label: 'Abmelden',
    hint: 'Sitzung',
    icon: LogOut,
    keywords: ['logout', 'ausloggen', 'sign out', 'abmelden'],
    run: ({ collapse }) => { collapse(); supabase.auth.signOut(); },
  },
];

// ---------------------------------------------------------------------------
// Kuratierte Teilmenge für leere Query (häufigste Befehle)
// ---------------------------------------------------------------------------
const CURATED_IDS = [
  'nav-dashboard',
  'nav-objekte',
  'nav-kontakte',
  'nav-tickets',
  'nav-rechnungen',
  'act-neuer-kontakt',
];

const CURATED_COMMANDS = CURATED_IDS
  .map((id) => COMMANDS.find((c) => c.id === id))
  .filter((c): c is Command => c !== undefined);

// ---------------------------------------------------------------------------
// Fuzzy-Suche
// ---------------------------------------------------------------------------

/**
 * Sucht Befehle per Fuzzy-Score.
 * - Leere / zu kurze Query (<1 Zeichen) → kuratierte Auswahl der 6 häufigsten.
 * - Sonst: alle Commands gegen Query scoren (Label + Keywords), null = kein
 *   Match, nach Score absteigend sortieren, auf 6 begrenzen.
 */
export function searchCommands(query: string): Command[] {
  const q = query.trim();
  if (q.length < 1) return CURATED_COMMANDS;

  return COMMANDS
    .map((cmd) => {
      const haystack = [cmd.label, ...(cmd.keywords ?? [])];
      const score = haystack.reduce<number | null>((best, h) => {
        const s = fuzzyScore(q, h);
        if (s === null) return best;
        return best === null ? s : Math.max(best, s);
      }, null);
      return { cmd, score };
    })
    .filter((x): x is { cmd: Command; score: number } => x.score !== null)
    .sort((a, b) => b.score - a.score)
    .slice(0, 6)
    .map((x) => x.cmd);
}
